import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  Cell
} from 'recharts';
import { Download } from 'lucide-react';
import { formatNumber, formatCurrency, downloadChartWithLogo, downloadChartAsImage, renderNikaoLogoBottomLeft } from '../utils/chartUtils';
import { nikaoColors } from '../theme/nikaoTheme';
import api from '../services/api';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

// Custom tick component for wrapping text
const CustomTick = ({ x, y, payload }) => {
  const text = payload.value;
  const maxLength = 12;
  
  if (text.length <= maxLength) {
    return (
      <text x={x} y={y + 5} textAnchor="middle" fontSize="10" fill={nikaoColors.tickText}>
        {text}
      </text>
    );
  }
  
  // Split text into multiple lines
  const words = text.split(' ');
  const lines = [];
  let currentLine = '';
  
  words.forEach(word => {
    if ((currentLine + word).length <= maxLength) {
      currentLine += (currentLine ? ' ' : '') + word;
    } else {
      if (currentLine) lines.push(currentLine);
      currentLine = word;
    }
  });
  if (currentLine) lines.push(currentLine);
  
  return (
    <g>
      {lines.map((line, index) => (
        <text 
          key={index}
          x={x} 
          y={y + 5 + (index * 14)} 
          textAnchor="middle" 
          fontSize="10" 
          fill={nikaoColors.tickText}
        >
          {line}
        </text>
      ))}
    </g>
  );
};

// Custom tick component for Capacity Calculation chart with longer text
const CapacityCustomTick = ({ x, y, payload }) => {
  const text = payload.value;
  const maxLength = 15;
  
  if (text.length <= maxLength) {
    return (
      <text x={x} y={y + 5} textAnchor="middle" fontSize="9" fill={nikaoColors.tickText}>
        {text}
      </text>
    );
  }
  
  // Split text into multiple lines
  const words = text.split(' ');
  const lines = [];
  let currentLine = '';
  
  words.forEach(word => {
    if ((currentLine + word).length <= maxLength) {
      currentLine += (currentLine ? ' ' : '') + word;
    } else {
      if (currentLine) lines.push(currentLine);
      currentLine = word;
    }
  });
  if (currentLine) lines.push(currentLine);
  
  return (
    <g>
      {lines.map((line, index) => (
        <text 
          key={index}
          x={x} 
          y={y + 5 + (index * 12)} 
          textAnchor="middle" 
          fontSize="9" 
          fill={nikaoColors.tickText}
        >
          {line}
        </text>
      ))}
    </g>
  );
};

const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    month: '', // Will be set to last month by default
    clientId: ''
  });
  const [clients, setClients] = useState([]);
  const [availableMonths, setAvailableMonths] = useState([]);
  const [chartData, setChartData] = useState({
    budgetVsActual: [],
    sowDaysComparison: [],
    resourceUtilization: [],
    revenueByResource: []
  });
  const navigate = useNavigate();

  // Generate last 12 months dynamically
  const generateLast12Months = () => {
    const months = [];
    const currentDate = new Date();
    
    for (let i = 0; i < 12; i++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const monthName = date.toLocaleString('default', { month: 'long' });
      
      months.push({
        value: `${year}-${month}`,
        label: `${monthName} ${year}`
      });
    }
    
    return months;
  };

  // Get last month as default
  const getLastMonth = () => {
    const currentDate = new Date();
    const lastMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1);
    const year = lastMonth.getFullYear();
    const month = String(lastMonth.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}`;
  };

  const loadDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Load all chart data in parallel
      const [budgetVsActualRes, sowDaysRes, resourceUtilRes, revenueByResourceRes] = await Promise.all([
        api.get('/dashboard/budget-vs-actual', { params: filters }),
        api.get('/dashboard/sow-days-comparison', { params: filters }),
        api.get('/dashboard/resource-utilization', { params: filters }),
        api.get('/dashboard/revenue-by-resource', { params: filters })
      ]);

      setChartData({
        budgetVsActual: budgetVsActualRes.data || [],
        sowDaysComparison: sowDaysRes.data || [],
        resourceUtilization: resourceUtilRes.data || [],
        revenueByResource: revenueByResourceRes.data || []
      });

    } catch (error) {
      console.error('Error loading dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    // Initialize months and set default to last month
    const months = generateLast12Months();
    setAvailableMonths(months);
    
    if (!filters.month) {
      const lastMonth = getLastMonth();
      setFilters(prev => ({ ...prev, month: lastMonth }));
    }
  }, [filters.month]);

  useEffect(() => {
    if (filters.month) {
      loadDashboardData();
      loadClients();
    }
  }, [filters, loadDashboardData]);

  const loadClients = async () => {
    try {
      const response = await api.get('/clients');
      setClients(response.data);
    } catch (error) {
      console.error('Error loading clients:', error);
      // If user is not authenticated, set empty clients array
      if (error.response?.status === 401) {
        setClients([]);
      }
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleAddSOW = () => {
    navigate('/sow');
  };

  // Utility function to calculate proper Y-axis domain using the 6-step algorithm
  const calculateYAxisDomain = (data, dataKey = 'value') => {
    if (!data || data.length === 0) return [0, 100];
    
    const values = data.map(item => item[dataKey] || 0);
    
    // Step 1: Find maximum value (always start from 0)
    const dataMax = Math.max(...values);
    
    // Handle edge case: all values are 0
    if (dataMax === 0) return [0, 100];
    
    // Step 2: Calculate data range from 0 to max
    const dataRange = dataMax - 0;
    
    // Step 3: Choose a nice tick spacing (aim for 5 ticks)
    const approxTickSpacing = dataRange / 5;
    const magnitude = Math.pow(10, Math.floor(Math.log10(approxTickSpacing)));
    
    // Find nice tick spacing (1, 2, or 5 * magnitude)
    const candidates = [1, 2, 5].map(n => n * magnitude);
    const tickSpacing = candidates.find(candidate => candidate >= approxTickSpacing) || candidates[candidates.length - 1];
    
    // Step 4: Determine axis boundaries (always start from 0)
    const yMin = 0;
    const yMax = Math.ceil(dataMax / tickSpacing) * tickSpacing;
    
    return [yMin, yMax];
  };

  // Utility function to calculate Y-axis domain for grouped data using the 6-step algorithm
  const calculateGroupedYAxisDomain = (data, dataKeys = ['capacity', 'planned', 'actual']) => {
    if (!data || data.length === 0) return [0, 100];
    
    // Step 1: Find maximum value across all data keys (always start from 0)
    let dataMax = 0;
    
    data.forEach(item => {
      dataKeys.forEach(key => {
        const value = item[key] || 0;
        if (value > dataMax) dataMax = value;
      });
    });
    
    // Handle edge case: all values are 0
    if (dataMax === 0) return [0, 100];
    
    // Step 2: Calculate data range from 0 to max
    const dataRange = dataMax - 0;
    
    // Step 3: Choose a nice tick spacing (aim for 6 ticks)
    const approxTickSpacing = dataRange / 6;
    const magnitude = Math.pow(10, Math.floor(Math.log10(approxTickSpacing)));
    
    // Find nice tick spacing (1, 2, or 5 * magnitude)
    const candidates = [1, 2, 5].map(n => n * magnitude);
    const tickSpacing = candidates.find(candidate => candidate >= approxTickSpacing) || candidates[candidates.length - 1];
    
    // Step 4: Determine axis boundaries (always start from 0)
    const yMin = 0;
    const yMax = Math.ceil(dataMax / tickSpacing) * tickSpacing;
    
    return [yMin, yMax];
  };

  // Utility function to generate tick marks using the proper algorithm
  const generateTicks = (yMin, yMax) => {
    // Step 2: Calculate data range
    const dataRange = yMax - yMin;
    
    // Step 3: Force exactly 6 ticks by using simple division
    const tickSpacing = dataRange / 5; // Divide by 5 to get 6 points (0, 1, 2, 3, 4, 5)
    
    // Step 4: Calculate tick points
    const ticks = [];
    for (let i = 0; i <= 5; i++) {
      const tick = yMin + (i * tickSpacing);
      ticks.push(Math.round(tick));
    }
    
    return ticks;
  };



  // Transform API data for charts
  const budgetVsActualData = chartData.budgetVsActual.length > 0 ? chartData.budgetVsActual : [
    { name: 'Budget forecast revenue', value: 0, color: nikaoColors.primary },
    { name: 'Committed SOW revenue', value: 0, color: nikaoColors.capacity },
    { name: 'Actual revenue', value: 0, color: nikaoColors.secondary }
  ];

  const sowDaysComparisonData = chartData.sowDaysComparison.length > 0 ? chartData.sowDaysComparison : [
    { name: 'Total net resource capacity (Days)', value: 0, color: nikaoColors.primary },
    { name: 'Total committed SOW resource capacity (Days)', value: 0, color: nikaoColors.capacity },
    { name: 'Actual Billed (Days)', value: 0, color: nikaoColors.secondary }
  ];

  const resourceUtilizationData = chartData.resourceUtilization.length > 0 ? chartData.resourceUtilization : [];

  const revenueByResourceData = chartData.revenueByResource.length > 0 ? chartData.revenueByResource : [];


  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with filters */}
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
        <div className="flex gap-4">
          <select
            value={filters.month}
            onChange={(e) => handleFilterChange('month', e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Months</option>
            {availableMonths.map(month => (
              <option key={month.value} value={month.value}>{month.label}</option>
            ))}
          </select>
          <select
            value={filters.clientId}
            onChange={(e) => handleFilterChange('clientId', e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Clients</option>
            {clients.map(client => (
              <option key={client.id} value={client.id}>{client.name}</option>
            ))}
          </select>
          <button
            onClick={handleAddSOW}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            Add SOW
          </button>
        </div>
      </div>

      {/* First Row: Budget vs Actual Revenue and SOW Days Comparison */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Budget vs Actual Revenue Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Budget vs Actual Revenue</span>
              <button
                onClick={() => {
                  downloadChartWithLogo('budget-vs-actual-chart', 'budget-vs-actual-revenue', 'Budget vs Actual Revenue')
                    .catch(error => {
                      console.error('Download failed:', error);
                      // Try fallback method
                      try {
                        downloadChartAsImage('budget-vs-actual-chart', 'budget-vs-actual-revenue');
                      } catch (fallbackError) {
                        console.error('Fallback download failed:', fallbackError);
                        toast.error('Download not available. Please use browser screenshot.');
                      }
                    });
                }}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <Download className="h-4 w-4" />
              </button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={500} id="budget-vs-actual-chart">
              <BarChart data={budgetVsActualData} margin={{ top: 10, right: 20, left: 20, bottom: 40 }} maxBarSize={60}>
                <CartesianGrid strokeDasharray="3 3" stroke={nikaoColors.border} horizontal={true} vertical={false} />
                <XAxis 
                  dataKey="name" 
                  angle={0}
                  textAnchor="middle"
                  height={80}
                  interval={0}
                  tick={{ fontSize: 12 }}
                />
                <YAxis 
                  domain={calculateYAxisDomain(budgetVsActualData)} 
                  tickFormatter={(value) => Math.round(value).toLocaleString()}
                  tick={{ fontSize: 12 }}
                  allowDecimals={false}
                  ticks={generateTicks(calculateYAxisDomain(budgetVsActualData)[0], calculateYAxisDomain(budgetVsActualData)[1])}
                />
                <Tooltip formatter={(value) => [formatCurrency(value), 'Revenue']} />
                <Bar 
                  dataKey="value" 
                  fill={(entry) => entry.color}
                  name="Revenue"
                  label={{ 
                    position: 'inside', 
                    formatter: (value) => formatCurrency(value),
                    style: { fill: 'white', fontSize: '12px', fontWeight: 'bold' }
                  }}
                >
                  {budgetVsActualData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Bar>
                {renderNikaoLogoBottomLeft()}
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Capacity Calculation Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Capacity Calculation</span>
              <button
                onClick={() => {
                  downloadChartWithLogo('sow-days-chart', 'sow-days-comparison', 'Capacity Calculation')
                    .catch(error => {
                      console.error('Download failed:', error);
                      // Try fallback method
                      try {
                        downloadChartAsImage('sow-days-chart', 'sow-days-comparison');
                      } catch (fallbackError) {
                        console.error('Fallback download failed:', fallbackError);
                        toast.error('Download not available. Please use browser screenshot.');
                      }
                    });
                }}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <Download className="h-4 w-4" />
              </button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={500} id="sow-days-chart">
              <BarChart data={sowDaysComparisonData} margin={{ top: 10, right: 20, left: 20, bottom: 40 }} maxBarSize={60}>
                <CartesianGrid strokeDasharray="3 3" stroke={nikaoColors.border} horizontal={true} vertical={false} />
                <XAxis 
                  dataKey="name" 
                  angle={0}
                  textAnchor="middle"
                  height={80}
                  interval={0}
                  tick={<CapacityCustomTick />}
                />
                <YAxis 
                  domain={calculateYAxisDomain(sowDaysComparisonData)} 
                  tickFormatter={(value) => Math.round(value).toLocaleString()}
                  tick={{ fontSize: 12 }}
                  allowDecimals={false}
                  ticks={generateTicks(calculateYAxisDomain(sowDaysComparisonData)[0], calculateYAxisDomain(sowDaysComparisonData)[1])}
                />
                <Tooltip formatter={(value) => [formatNumber(value), 'Days']} />
                <Bar 
                  dataKey="value" 
                  fill={(entry) => entry.color}
                  name="Days"
                  label={{ 
                    position: 'inside', 
                    formatter: (value) => Math.round(value).toString(),
                    style: { fill: 'white', fontSize: '12px', fontWeight: 'bold' }
                  }}
                >
                  {sowDaysComparisonData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Bar>
                {renderNikaoLogoBottomLeft()}
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Second Row: Resource Utilization */}
      <div className="mt-4">
        <Card>
          <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Capacity vs SOW Planned Days vs Actual Billed Days</span>
            <button
                              onClick={() => {
                  downloadChartWithLogo('resource-utilization-chart', 'resource-utilization', 'Capacity vs SOW Planned Days vs Actual Billed Days')
                    .catch(error => {
                      console.error('Download failed:', error);
                      // Try fallback method
                      try {
                        downloadChartAsImage('resource-utilization-chart', 'resource-utilization');
                      } catch (fallbackError) {
                        console.error('Fallback download failed:', fallbackError);
                        toast.error('Download not available. Please use browser screenshot.');
                      }
                    });
                }}
              className="p-1 hover:bg-gray-100 rounded"
            >
              <Download className="h-4 w-4" />
            </button>
          </CardTitle>
          </CardHeader>
          <CardContent>
          <div style={{ position: 'relative' }}>
            <ResponsiveContainer width="100%" height={500} id="resource-utilization-chart">
              <BarChart data={resourceUtilizationData}  margin={{ top: 10, right: 20, left: 20, bottom: 100 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={nikaoColors.border} horizontal={true} vertical={false} />
                <XAxis 
                  dataKey="name" 
                  height={100}
                  interval={0}
                  tick={<CustomTick />}
                />
                <YAxis 
                  domain={calculateGroupedYAxisDomain(resourceUtilizationData)} 
                  tickFormatter={(value) => Math.round(value)} 
                  allowDecimals={false}
                  ticks={generateTicks(calculateGroupedYAxisDomain(resourceUtilizationData)[0], calculateGroupedYAxisDomain(resourceUtilizationData)[1])}
                />
                <Tooltip formatter={(value) => [formatNumber(value), 'Days']} />
                <Legend />
                <Bar 
                  dataKey="capacity" 
                  fill={nikaoColors.primary} 
                  name="Total net resource capacity (Days)"
                  label={{ 
                    position: 'inside', 
                    formatter: (value) => Math.round(value).toString(),
                    style: { fill: 'white', fontSize: '12px', fontWeight: 'bold' }
                  }}
                />
                <Bar 
                  dataKey="planned" 
                  fill={nikaoColors.capacity} 
                  name="Total committed SOW resource capacity (Days)"
                  label={{ 
                    position: 'inside', 
                    formatter: (value) => Math.round(value).toString(),
                    style: { fill: 'white', fontSize: '12px', fontWeight: 'bold' }
                  }}
                />
                <Bar 
                  dataKey="actual" 
                  fill={nikaoColors.secondary} 
                  name="Actual Billed (Days)"
                  label={{ 
                    position: 'inside', 
                    formatter: (value) => Math.round(value).toString(),
                    style: { fill: 'white', fontSize: '12px', fontWeight: 'bold' }
                  }}
                />
                {renderNikaoLogoBottomLeft()}
              </BarChart>
            </ResponsiveContainer>
          </div>
          </CardContent>
        </Card>
      </div>

      {/* Third Row: Forecast Revenue by Resource Type */}
      <div className="mt-4">
        <Card>
          <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Forecast Revenue by Resource Type</span>
            <button
                              onClick={() => {
                  downloadChartWithLogo('revenue-by-resource-chart', 'revenue-by-resource', 'Forecast Revenue by Resource Type')
                    .catch(error => {
                      console.error('Download failed:', error);
                      // Try fallback method
                      try {
                        downloadChartAsImage('revenue-by-resource-chart', 'revenue-by-resource');
                      } catch (fallbackError) {
                        console.error('Fallback download failed:', fallbackError);
                        toast.error('Download not available. Please use browser screenshot.');
                      }
                    });
                }}
              className="p-1 hover:bg-gray-100 rounded"
            >
              <Download className="h-4 w-4" />
            </button>
          </CardTitle>
          </CardHeader>
          <CardContent>
          <div style={{ position: 'relative' }}>
            <ResponsiveContainer width="100%" height={500} id="revenue-by-resource-chart">
              <BarChart data={revenueByResourceData} margin={{ top: 10, right: 20, left: 20, bottom: 100 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={nikaoColors.border} horizontal={true} vertical={false} />
                <XAxis 
                  dataKey="name" 
                  height={100}
                  interval={0}
                  tick={<CustomTick />}
                />
                <YAxis 
                  domain={calculateGroupedYAxisDomain(revenueByResourceData, ['budgetForecast', 'committedSow', 'actual'])} 
                  tickFormatter={(value) => Math.round(value).toLocaleString()}
                  tick={{ fontSize: 12 }}
                  allowDecimals={false}
                  ticks={generateTicks(calculateGroupedYAxisDomain(revenueByResourceData, ['budgetForecast', 'committedSow', 'actual'])[0], calculateGroupedYAxisDomain(revenueByResourceData, ['budgetForecast', 'committedSow', 'actual'])[1])}
                />
                <Tooltip formatter={(value) => [formatCurrency(value), 'Revenue']} />
                <Legend />
                <Bar 
                  dataKey="budgetForecast" 
                  fill={nikaoColors.primary} 
                  name="Budget forecast revenue"
                  maxBarSize={80}
                  label={{ 
                    position: 'inside', 
                    formatter: (value) => formatCurrency(value),
                    style: { fill: 'white', fontSize: '10px', fontWeight: 'bold' }
                  }}
                />
                <Bar 
                  dataKey="committedSow" 
                  fill={nikaoColors.capacity} 
                  name="Committed SOW revenue"
                  maxBarSize={80}
                  label={{ 
                    position: 'inside', 
                    formatter: (value) => formatCurrency(value),
                    style: { fill: 'white', fontSize: '10px', fontWeight: 'bold' }
                  }}
                />
                <Bar 
                  dataKey="actual" 
                  fill={nikaoColors.secondary} 
                  name="Actual Revenue"
                  maxBarSize={80}
                  label={{ 
                    position: 'inside', 
                    formatter: (value) => formatCurrency(value),
                    style: { fill: 'white', fontSize: '10px', fontWeight: 'bold' }
                  }}
                />
                {renderNikaoLogoBottomLeft()}
              </BarChart>
            </ResponsiveContainer>
          </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard; 